import React, { useState } from "react";
import {
  Dialog,
  InputBase,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PdfPreviewModal from "../Common/PdfPreviewModal";
import ImagePreviewModal from "../Common/ImagePreviewModal";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDownloadDocument, useSmartSearch } from "../../Query/useCompanies";
import {
  downloadDocumentBlob,
  isPreviewableFile,
} from "../../Helpers/documentHelper";
import color from "../../Constants/colors";
import { SmartSearchDocument } from "../../Models/smartSearch";

interface SmartSearchProps {
  open: boolean;
  onClose: () => void;
}

const SmartSearch: React.FC<SmartSearchProps> = ({ open, onClose }) => {
  const { t } = useTranslation("singleCompanyComponent");
  const { companyId } = useParams<{ companyId: string }>();
  const [inputValue, setInputValue] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState<number | null>(
    null,
  );
  const [previewDocumentName, setPreviewDocumentName] = useState<
    string | undefined
  >(undefined);
  const { data, isLoading, isFetching } = useSmartSearch(
    companyId!,
    submittedQuery,
    !!submittedQuery,
  );
  const downloadDocumentMutation = useDownloadDocument();

  const handlePreviewDocument = (documentId: number, documentName: string) => {
    if (!isPreviewableFile(documentName)) return;

    setPreviewDocumentId(documentId);
    setPreviewDocumentName(documentName);

    if (documentName.toLowerCase().endsWith(".pdf")) {
      setPdfPreviewOpen(true);
    } else {
      setImagePreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPdfPreviewOpen(false);
    setImagePreviewOpen(false);
    setPreviewDocumentId(null);
    setPreviewDocumentName(undefined);
  };

  const handleDownloadDocument = (documentId: number, fileName: string) => {
    downloadDocumentMutation.mutate(
      { documentId },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, fileName);
        },
      },
    );
  };

  const formatChunk = (text: string) => {
    return text.length > 70 ? `${text.substring(0, 70)}...` : text;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && inputValue.trim()) {
      setSubmittedQuery(inputValue);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          position: "fixed",
          top: "15%",
          m: 0,
          background: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        },
      }}
    >
      <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
        <SearchIcon sx={{ color: "text.secondary", mr: 1.5, fontSize: 28 }} />
        <InputBase
          fullWidth
          autoFocus
          placeholder={t("companyActions.smartSearch.searchPlaceHolder")}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ fontSize: "1.2rem" }}
        />
        {(isLoading || isFetching) && (
          <CircularProgress size={20} sx={{ color: color.accent }} />
        )}
      </Box>

      {(data?.data || isLoading) && <Divider />}

      <Box sx={{ maxHeight: "400px", overflow: "auto" }}>
        {data?.data && data.data.length > 0 ? (
          <List sx={{ py: 0 }}>
            {data.data.map((doc: SmartSearchDocument, index: number) => (
              <ListItem
                key={`${doc.document_id}-${index}`}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                }}
                secondaryAction={
                  doc.document_type !== 0.0 && (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {isPreviewableFile(doc.title) && (
                        <IconButton
                          edge="end"
                          onClick={() =>
                            handlePreviewDocument(doc.document_id, doc.title)
                          }
                          sx={{ color: color.accent }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        onClick={() =>
                          handleDownloadDocument(doc.document_id, doc.title)
                        }
                        sx={{ color: color.accent, mr: 1 }}
                        disabled={
                          downloadDocumentMutation.isPending &&
                          downloadDocumentMutation.variables?.documentId ===
                            doc.document_id
                        }
                      >
                        {downloadDocumentMutation.isPending &&
                        downloadDocumentMutation.variables?.documentId ===
                          doc.document_id ? (
                          <CircularProgress
                            size={24}
                            sx={{ color: color.accent }}
                          />
                        ) : (
                          <DownloadIcon />
                        )}
                      </IconButton>
                    </Box>
                  )
                }
              >
                <InsertDriveFileIcon
                  sx={{ mr: 2, color: "primary.main", opacity: 0.8 }}
                />
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {doc.title}
                    </Typography>
                  }
                  secondary={
                    <Box component="span" sx={{ display: "block" }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, fontStyle: "italic", display: "block" }}
                      >
                        {formatChunk(doc.chunk)}
                      </Typography>

                      {doc.pages && doc.pages.length > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.3, display: "block" }}
                        >
                          {t("companyActions.smartSearch.pages")}
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            {doc.pages.join(", ")}
                          </Box>
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : submittedQuery && !isLoading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              {t("companyActions.smartSearch.noDocumentsFound")}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ p: 1, px: 2, backgroundColor: "rgba(0,0,0,0.02)" }}>
        <Typography variant="caption" color="text.disabled">
          {t("companyActions.smartSearch.press")}{" "}
          <b>{t("companyActions.smartSearch.enter")}</b>{" "}
          {t("companyActions.smartSearch.toSearch")}
        </Typography>
      </Box>

      <PdfPreviewModal
        open={pdfPreviewOpen}
        onClose={handleClosePreview}
        documentId={previewDocumentId}
        documentName={previewDocumentName}
      />
      <ImagePreviewModal
        open={imagePreviewOpen}
        onClose={handleClosePreview}
        documentId={previewDocumentId}
        documentName={previewDocumentName}
      />
    </Dialog>
  );
};

export default SmartSearch;
